import { Component, computed, effect, inject, OnInit, signal, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import {
  createDynamicFilterState,
  DynamicFilterConfig,
  DynamicFilterValues,
  EnumValues,
  ModalService,
  SiteService,
} from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';
import { forkJoin, finalize, of, Subject } from 'rxjs';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { SelectItemGroup, SortEvent } from 'primeng/api';
import { PaginatorState } from 'primeng/paginator';
import _ from 'lodash';
import { Certification } from '../../../models/certification';
import { DataMasterAdminGroup, DataMasterViewerGroup } from '../../../models/authorization/enum/user-group.enum';
import { ItemMaster, ItemCategory, ItemMasterFilter } from '../../../models/item-master';
import { InventoryType } from '../../../models/inventory/inventory-type';
import { ItemMasterProposal, ItemMasterProposalStatus } from '../../../models/item-master-proposal';
import { EnumService } from '../../../services/enum.service';
import { LocaleHelperService } from '../../../services/localehelperservice.service';
import { CertificationProxyService } from '../../../proxies/item/certification-proxy.service';
import { InventoryTypeProxyService } from '../../../proxies/inventory/inventory-type.proxy.service';
import { ItemCategoryProxyService } from '../../../proxies/item/item-category.proxy.service';
import { ItemMasterProxyService } from '../../../proxies/item/item-master.proxy.service';
import { ItemMasterProposalProxyService } from '../../../proxies/item/item-master-proposal.proxy.service';
import { ItemMasterModalComponent } from '../../modals/item-master-modal/item-master-modal.component';

@Component({
  selector: 'app-item-master',
  templateUrl: './item-master.component.html',
  styleUrl: './item-master.component.scss',
})
export class ItemMasterComponent implements OnInit, OnDestroy {
  private localeHelperService = inject(LocaleHelperService);
  private translateService = inject(TranslateService);
  private enumService = inject(EnumService);
  private modalService = inject(ModalService);
  private certificationProxyService = inject(CertificationProxyService);
  private inventoryTypeProxyService = inject(InventoryTypeProxyService);
  private itemMasterProxyService = inject(ItemMasterProxyService);
  private itemMasterProposalProxyService = inject(ItemMasterProposalProxyService);
  private itemCategoryProxyService = inject(ItemCategoryProxyService);

  private destroy$ = new Subject<void>();
  private filtersPanelInitialized = false;

  isLoading = signal(0);
  filterConfig = signal<DynamicFilterConfig | null>(null);
  lookupsLoaded = signal(false);
  readonly filterState = createDynamicFilterState({
    storageKey: 'item-master-filters',
    config: computed(() => (this.lookupsLoaded() ? this.filterConfig() : null)),
  });
  showFilters = signal(false);

  allItems = signal<ItemMaster[]>([]);
  allInventoryTypes = signal<InventoryType[]>([]);
  allItemCategories = signal<ItemCategory[]>([]);
  allItemProposals = signal<ItemMasterProposal[]>([]);
  firstPaginationProposals = signal<number>(0);
  showProposalFilter = signal<boolean>(false);
  proposalItemCodeFilter = signal<string>('');

  filteredItemProposals = computed(() => {
    const filter = this.proposalItemCodeFilter().toLowerCase().trim();
    if (!filter) return this.allItemProposals();
    return this.allItemProposals().filter((p) => p.itemCode?.toLowerCase().includes(filter));
  });
  certifications = signal<Certification[]>([]);

  // Pagination
  currentPage = signal(1);
  pageSize = signal(25);
  totalRecords = signal(0);

  // Sorting
  sortField = signal<string | undefined>(undefined);
  sortOrder = signal<number>(-1);

  private lastSearchCriteria: ItemMasterFilter = {};

  filteredInventoryTypes = signal<InventoryType[]>([]);
  filteredItemCategories = signal<ItemCategory[]>([]);
  filteredItemLowestSubCategories = signal<ItemCategory[]>([]);

  groupedLvl2ItemCategories: SelectItemGroup[] = [];

  uoms = signal<EnumValues>({});
  statuses = signal<EnumValues>({});
  itemMasterProposalStatuses = signal<EnumValues>({});
  enumsReady = signal(false);

  displayMode: 'item-master' | 'item-master-proposal' = 'item-master';
  public viewModeOptions = [
    { label: 'Normal', value: 'item-master' },
    { label: 'Proposal', value: 'item-master-proposal' },
  ];

  inventoryTypesMap: Record<string, InventoryType> = {};
  itemCategoriesMap: Record<string, ItemCategory> = {};

  get standardViewMode(): boolean {
    return this.displayMode === 'item-master';
  }

  get subTitle(): string {
    return this.standardViewMode ? '' : ': Proposal(s)';
  }

  canEdit = computed(() => {
    if (AuthService.hasRole(DataMasterAdminGroup.ItemMasterAdmin)) {
      return true;
    }
    if (AuthService.hasRole(DataMasterViewerGroup.ItemMasterViewer)) {
      return false;
    }
    return false;
  });

  canAssignSites = computed(() => AuthService.hasRole(DataMasterAdminGroup.ItemSiteAssigner));
  private useAllSitesInSearch = AuthService.hasGlobalRole(DataMasterAdminGroup.ItemSiteAssigner);

  getAssignedSiteNames(item: ItemMaster): string {
    if (!item.siteIds || item.siteIds.length === 0) return '';
    const allSites = SiteService.getSites();
    return item.siteIds
      .map((id) => allSites.find((s) => s.id === id)?.name ?? '')
      .filter((name) => name)
      .join(', ');
  }

  getRowData(rowData: unknown): ItemMaster {
    return rowData as ItemMaster;
  }

  getProposalRowData(rowData: unknown): ItemMasterProposal {
    return rowData as ItemMasterProposal;
  }

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => {
      if (!this.filtersPanelInitialized) {
        this.filtersPanelInitialized = true;
        this.showFilters.set(this.filterState.hasActive());
      }
      this.searchWithFilters(filters);
    });

    effect(
      () => {
        const statusesValue = this.enumService.getEnum('ItemStatus', 'itemEnums').values;
        const uomsValue = this.enumService.getEnum('UnitOfMeasure', 'itemEnums').values;
        const proposalStatusesValue = this.enumService.getEnum('ItemMasterProposalStatus', 'itemEnums').values;

        if (Object.keys(statusesValue).length > 0 && Object.keys(uomsValue).length > 0) {
          this.statuses.set(statusesValue);
          this.uoms.set(uomsValue);
          this.itemMasterProposalStatuses.set(proposalStatusesValue);
          this.enumsReady.set(true);
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        if (this.translateService.currentLang) {
          this.tryInitializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    this.loadInitialData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private startLoading(): void {
    this.isLoading.update((val) => val + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((val) => val - 1);
  }

  private tryInitializeFilterConfig(): void {
    if (this.translateService.currentLang) {
      this.initializeFilterConfig();
    }
  }

  private initializeFilterConfig(): void {
    const translate = (key: string, fallback: string) => {
      const translation = this.translateService.instant(key);
      return translation === key ? fallback : translation;
    };

    const config: DynamicFilterConfig = {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'itemCode',
          label: translate('ItemMaster_Code', 'Item Code'),
          placeholder: translate('Enter_item_code', 'Enter item code'),
          defaultVisible: true,
        },
        {
          type: 'text',
          formControlName: 'name',
          label: translate('ItemMaster_Name', 'Name'),
          placeholder: translate('Enter_item_name', 'Enter item name'),
          defaultVisible: true,
        },
        {
          type: 'text',
          formControlName: 'description',
          label: translate('ItemMaster_Description', 'Description'),
          placeholder: translate('Enter_description', 'Enter description'),
          defaultVisible: false,
        },
        {
          type: 'text',
          formControlName: 'baseFabricCode',
          label: translate('ItemMaster_BaseFabricCode', 'Base Fabric Code'),
          placeholder: translate('Enter_base_fabric_code', 'Enter base fabric code'),
          defaultVisible: false,
        },
        {
          type: 'text',
          formControlName: 'baseGreigeCode',
          label: translate('ItemMaster_BaseGreigeCode', 'Greige Code'),
          placeholder: translate('Enter_greige_code', 'Enter greige code'),
          defaultVisible: false,
        },
        {
          type: 'text',
          formControlName: 'baseYarnCode',
          label: translate('ItemMaster_BaseYarnCode', 'Yarn Code'),
          placeholder: translate('Enter_yarn_code', 'Enter yarn code'),
          defaultVisible: false,
        },
        {
          type: 'autocomplete',
          formControlName: 'inventoryTypeReference',
          label: translate('ItemMaster_InventoryType', 'Inventory Type'),
          placeholder: translate('Select_inventory_type', 'Select inventory type'),
          autocompleteConfig: {
            suggestions: this.filteredInventoryTypes(),
            optionLabel: 'fieldToDisplay',
            optionValue: 'id',
            completeMethod: (event: AutoCompleteCompleteEvent) => this.filterInventoryTypes(event),
            resolveById: (id: string) => of(this.inventoryTypesMap[id] ?? null),
            dropdown: true,
            forceSelection: true,
          },
          showClear: true,
          defaultVisible: false,
        },
        {
          type: 'autocomplete',
          formControlName: 'itemCategoryCode',
          label: translate('ItemMaster_Category', 'Category'),
          placeholder: translate('Select_category', 'Select category'),
          autocompleteConfig: {
            suggestions: this.filteredItemCategories(),
            optionLabel: 'fieldToDisplay',
            optionValue: 'code',
            completeMethod: (event: AutoCompleteCompleteEvent) => this.filterItemCategories(event),
            resolveById: (code: string) => of(this.itemCategoriesMap[code] ?? null),
            dropdown: true,
            forceSelection: true,
          },
          showClear: true,
          defaultVisible: false,
        },
        {
          type: 'autocomplete',
          formControlName: 'itemLowestSubCategoryCode',
          label: translate('ItemMaster_SubCategory', 'Sub Category'),
          placeholder: translate('Select_subcategory', 'Select subcategory'),
          autocompleteConfig: {
            suggestions: this.filteredItemLowestSubCategories(),
            optionLabel: 'fieldToDisplay',
            optionValue: 'code',
            completeMethod: (event: AutoCompleteCompleteEvent) => this.filterItemLowestSubCategories(event),
            resolveById: (code: string) => of(this.allItemCategories().find((cat) => cat.code === code && cat.level >= 2) ?? null),
            dropdown: true,
            forceSelection: true,
          },
          showClear: true,
          defaultVisible: false,
        },
      ],
    };

    this.filterConfig.set(config);
  }

  loadInitialData(): void {
    this.startLoading();
    // The lookups below resolve the code/name columns of every row. When the search
    // spans both sites, they have to span both sites too — otherwise a row from the
    // non-selected site finds nothing in the map and renders blank.
    forkJoin({
      inventoryTypes: this.inventoryTypeProxyService.getAllInventoryType(undefined, this.useAllSitesInSearch),
      itemCategories: this.itemCategoryProxyService.getAllItemsCategories(undefined, this.useAllSitesInSearch),
      certifications: this.certificationProxyService.getAllCertifications(this.useAllSitesInSearch),
      itemProposals: this.itemMasterProposalProxyService.getAll(),
    })
      .pipe(
        finalize(() => {
          this.stopLoading();
          this.lookupsLoaded.set(true);
        }),
      )
      .subscribe({
        next: ({ inventoryTypes, itemCategories, certifications, itemProposals }) => {
          const allInventoryTypes = (inventoryTypes.data || []).map((it) => ({
            ...it,
            fieldToDisplay: this.localeHelperService.getLocalizedValue(it.name),
          }));

          const allCategories = (itemCategories.data || []).map((cat) => ({
            ...cat,
            fieldToDisplay: `${cat.code} - ${this.localeHelperService.getLocalizedValue(cat.name)}`,
          }));

          this.allInventoryTypes.set(allInventoryTypes);
          this.allItemCategories.set(allCategories);
          this.certifications.set(certifications.data || []);
          this.allItemProposals.set((itemProposals.data || []).filter((p) => p.status === ItemMasterProposalStatus.Pending));

          this.filteredInventoryTypes.set(allInventoryTypes);
          this.filteredItemCategories.set(allCategories);
          this.filteredItemLowestSubCategories.set(allCategories.filter((c) => c.level >= 2));

          this.inventoryTypesMap = allInventoryTypes.reduce(
            (acc, it) => {
              if (it.id) {
                acc[it.id] = it;
              }
              return acc;
            },
            {} as Record<string, InventoryType>,
          );

          this.itemCategoriesMap = allCategories.reduce(
            (acc, cat) => {
              if (cat.code) {
                acc[cat.code] = cat;
              }
              return acc;
            },
            {} as Record<string, ItemCategory>,
          );

          this.groupedLvl2ItemCategories = allCategories
            .filter((ic) => ic.level === 2)
            .map((ic) => ({
              value: ic.code,
              label: `${ic.code} - ${this.localeHelperService.getLocalizedValue(ic.name)}`,
              items: this.getChildrenCategories(ic.code, `${ic.code} - ${this.localeHelperService.getLocalizedValue(ic.name)}`),
            }));

          this.tryInitializeFilterConfig();
        },
      });
  }

  onSearch(searchCriteria: ItemMasterFilter): void {
    this.lastSearchCriteria = searchCriteria;
    this.startLoading();
    this.itemMasterProxyService
      .search(searchCriteria, this.currentPage(), this.pageSize(), this.sortField(), this.sortOrder(), true, this.useAllSitesInSearch)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          this.allItems.set(response?.data?.items || []);
          this.totalRecords.set(response?.data?.totalCount || 0);
        },
        error: () => {
          this.allItems.set([]);
          this.totalRecords.set(0);
        },
      });
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage.set((event.page || 0) + 1);
    this.pageSize.set(event.rows || this.pageSize());
    this.onSearch(this.lastSearchCriteria);
  }

  onSort(event: SortEvent): void {
    this.sortField.set(event.field);
    this.sortOrder.set(event.order ?? -1);
    this.currentPage.set(1);
    this.onSearch(this.lastSearchCriteria);
  }

  onPageChangeProposals(event?: { first?: number }) {
    this.firstPaginationProposals.set(event?.first || 0);
  }

  filterInventoryTypes(event: AutoCompleteCompleteEvent): void {
    const query = event.query.toLowerCase();
    const filtered = this.allInventoryTypes().filter(
      (it) =>
        this.localeHelperService.getLocalizedValue(it.name).toLowerCase().includes(query) || it.description?.toLowerCase().includes(query),
    );
    this.filteredInventoryTypes.set(filtered);
  }

  filterItemCategories(event: AutoCompleteCompleteEvent): void {
    const query = event.query.toLowerCase();
    const filtered = this.allItemCategories().filter(
      (cat) => this.localeHelperService.getLocalizedValue(cat.name).toLowerCase().includes(query) || cat.code.toLowerCase().includes(query),
    );
    this.filteredItemCategories.set(filtered);
  }

  filterItemLowestSubCategories(event: AutoCompleteCompleteEvent): void {
    const query = event.query.toLowerCase();
    const filtered = this.allItemCategories().filter(
      (cat) =>
        cat.level >= 2 &&
        (this.localeHelperService.getLocalizedValue(cat.name).toLowerCase().includes(query) || cat.code.toLowerCase().includes(query)),
    );
    this.filteredItemLowestSubCategories.set(filtered);
  }

  getChildrenCategories(parentCode: string, label: string): { label: string; value: string; index?: number }[] {
    return [
      ...[{ label: `${label}`, value: parentCode, index: 0 }],
      ...(this.allItemCategories()
        .filter((ic: ItemCategory) => ic.parentCode === parentCode)
        .map((sc: ItemCategory) => ({
          value: sc.code,
          label: `${sc.code} - ${this.localeHelperService.getLocalizedValue(sc.name)}`,
        })) || []),
    ];
  }

  private searchWithFilters(values: DynamicFilterValues): void {
    const filters = values as Record<string, string | null | undefined>;
    const searchCriteria: ItemMasterFilter = {
      name: filters['name']?.trim() || undefined,
      itemCode: filters['itemCode']?.trim() || undefined,
      description: filters['description']?.trim() || undefined,
      itemCategoryCode: filters['itemCategoryCode'] || undefined,
      itemLowestSubCategoryCode: filters['itemLowestSubCategoryCode'] || undefined,
      inventoryTypeReference: filters['inventoryTypeReference'] || '',
      baseFabricCode: filters['baseFabricCode']?.trim() || undefined,
      baseGreigeCode: filters['baseGreigeCode']?.trim() || undefined,
      baseYarnCode: filters['baseYarnCode']?.trim() || undefined,
    };

    this.currentPage.set(1);
    this.onSearch(searchCriteria);
  }

  getCategoryCode(categoryCode: string | undefined | null): string {
    if (!categoryCode) return '';
    const category = this.itemCategoriesMap[categoryCode];
    return category ? `${category.code} - ${this.localeHelperService.getLocalizedValue(category.name)}` : '';
  }

  getInventoryType(inventoryTypeId: string | undefined | null): string {
    if (!inventoryTypeId) return '';
    const inventoryType = this.inventoryTypesMap[inventoryTypeId];
    return inventoryType ? this.localeHelperService.getLocalizedValue(inventoryType.name) : '';
  }

  getUnitName(uomKey: number): string {
    if (uomKey === null || uomKey === undefined) return '';
    return this.uoms()[uomKey] || '';
  }

  getStatusName(status: number): string {
    if (status === null || status === undefined) return '';
    return this.statuses()[status] || '';
  }

  openItemModal(itemMaster?: ItemMaster, itemMasterProposal?: ItemMasterProposal, duplicateItemMaster?: boolean): void {
    this.modalService
      .open(
        ItemMasterModalComponent,
        { modalClass: 'modal_large' },
        {
          itemMaster: structuredClone(itemMaster),
          certifications: this.certifications(),
          itemMasterProposal: itemMasterProposal,
          duplicateItemMaster: duplicateItemMaster,
          uoms: this.uoms(),
          canEdit: this.canEdit(),
        },
      )
      .subscribe((result) => {
        if (result && result.action === 'save' && result.data) {
          const savedItem = result.data;
          this.allItems.update((currentItems) => {
            const index = currentItems.findIndex((p) => p.id === savedItem.id);
            if (index !== -1) {
              const newItems = [...currentItems];
              newItems[index] = savedItem;
              return newItems;
            } else {
              return [...currentItems, savedItem];
            }
          });

          if (!_.isNil(itemMasterProposal)) {
            this.allItemProposals.update((currentProposals) => currentProposals.filter((p) => p.id !== itemMasterProposal.id));
          }
        }
      });
  }
}
